import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VerifiedIcon from '@mui/icons-material/Verified';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { aiSafety } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import { useIsMobile } from '@/hooks/useIsMobile';

type SafetyCaseStatus = 'draft' | 'submitted' | 'approved' | 'retired';

type SafetyCase = {
  id: number;
  capability: number;
  title: string;
  hazard_analysis: string;
  mitigations: string;
  validation_evidence: string;
  post_market_monitoring: string;
  status: SafetyCaseStatus;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
};

type CapabilityRow = { id: number; code: string; display_name: string };

const STATUS_META: Record<SafetyCaseStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' }> = {
  draft:     { label: 'draft',     color: 'default' },
  submitted: { label: 'submitted', color: 'warning' },
  approved:  { label: 'approved',  color: 'success' },
  retired:   { label: 'retired',   color: 'default' },
};

export function AISafetyCasesPage() {
  const q = useApiQuery({
    queryKey: ['ai-safety', 'safety-cases'],
    queryFn:  aiSafety.safetyCases.list as () => Promise<SafetyCase[]>,
  });
  const capsQ = useApiQuery({
    queryKey: ['ai-safety', 'capabilities'],
    queryFn:  aiSafety.capabilities.list as () => Promise<CapabilityRow[]>,
  });

  const [editing, setEditing] = useState<SafetyCase | null>(null);
  const [creating, setCreating] = useState(false);
  const [approving, setApproving] = useState<SafetyCase | null>(null);

  const qc = useQueryClient();
  const toast = useToast();

  const approveMut = useApiMutation({
    mutationFn: (vars: { id: number; reason?: string }) =>
      aiSafety.safetyCases.approve(vars.id, vars.reason),
    onSuccess: () => {
      toast.success('Safety case approved.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'safety-cases'] });
      setApproving(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const capabilityMap = new Map((capsQ.data ?? []).map((c) => [c.id, c]));

  return (
    <>
      <PageHeader
        title="Safety cases"
        subtitle="Hazard analysis + mitigations + validation evidence + post-market monitoring per capability."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New safety case
          </Button>
        }
      />

      <DataState query={q} empty={{
        title: 'No safety cases yet',
        description: 'Every clinical AI capability needs an approved safety case before activation.',
      }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((s) => (
              <Row key={s.id} safetyCase={s}
                   capabilityName={capabilityMap.get(s.capability)?.display_name ?? `#${s.capability}`}
                   onEdit={() => setEditing(s)}
                   onApprove={() => setApproving(s)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <SafetyCaseDialog
          safetyCase={editing}
          capabilities={capsQ.data ?? []}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}

      <ConfirmDialog
        open={approving !== null}
        title={`Approve "${approving?.title}"?`}
        description="Approval requires that hazard analysis, mitigations, validation evidence and post-market monitoring are all populated. Once approved, the linked capability becomes activatable."
        confirmLabel="Approve"
        onCancel={() => setApproving(null)}
        onConfirm={(reason) =>
          approving
            ? approveMut.mutateAsync({ id: approving.id, reason: reason ?? '' })
            : Promise.resolve()
        }
      />
    </>
  );
}

function Row({
  safetyCase, capabilityName, onEdit, onApprove,
}: {
  safetyCase: SafetyCase;
  capabilityName: string;
  onEdit: () => void;
  onApprove: () => void;
}) {
  const sm = STATUS_META[safetyCase.status];
  return (
    <Card sx={{ borderColor: safetyCase.status === 'approved' ? 'success.main' : 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: safetyCase.status === 'approved' ? 'success.main' : 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <HealthAndSafetyIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {safetyCase.title}
              </Typography>
              <Chip size="small" label={sm.label} color={sm.color} variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              for capability: {capabilityName}
              {safetyCase.approved_at && (
                <> · approved <span className="tabular">{formatDateTime(safetyCase.approved_at)}</span></>
              )}
            </Typography>
          </Box>
          {safetyCase.status === 'submitted' && (
            <Button size="small" variant="contained" color="success"
                    startIcon={<VerifiedIcon />}
                    onClick={onApprove}>
              Approve
            </Button>
          )}
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            {safetyCase.status === 'approved' ? 'View' : 'Edit'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SafetyCaseDialog({
  safetyCase, capabilities, onClose,
}: {
  safetyCase: SafetyCase | null;
  capabilities: CapabilityRow[];
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = safetyCase !== null;
  const isReadOnly = safetyCase?.status === 'approved' || safetyCase?.status === 'retired';

  const [capabilityId, setCapabilityId] = useState(
    safetyCase?.capability ? String(safetyCase.capability) : '');
  const [title, setTitle] = useState(safetyCase?.title ?? '');
  const [hazardAnalysis, setHazardAnalysis] = useState(safetyCase?.hazard_analysis ?? '');
  const [mitigations, setMitigations] = useState(safetyCase?.mitigations ?? '');
  const [validation, setValidation] = useState(safetyCase?.validation_evidence ?? '');
  const [postMarket, setPostMarket] = useState(safetyCase?.post_market_monitoring ?? '');
  const [submit, setSubmit] = useState(false);

  useEffect(() => {
    setCapabilityId(safetyCase?.capability ? String(safetyCase.capability) : '');
    setTitle(safetyCase?.title ?? '');
    setHazardAnalysis(safetyCase?.hazard_analysis ?? '');
    setMitigations(safetyCase?.mitigations ?? '');
    setValidation(safetyCase?.validation_evidence ?? '');
    setPostMarket(safetyCase?.post_market_monitoring ?? '');
    setSubmit(false);
  }, [safetyCase?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        capability: parseInt(capabilityId, 10),
        title,
        hazard_analysis: hazardAnalysis,
        mitigations,
        validation_evidence: validation,
        post_market_monitoring: postMarket,
      };
      if (submit) payload.status = 'submitted';
      return isEdit
        ? aiSafety.safetyCases.update(safetyCase!.id, payload)
        : aiSafety.safetyCases.create(payload);
    },
    onSuccess: () => {
      toast.success(submit ? 'Safety case submitted for approval.'
                            : isEdit ? 'Safety case saved.'
                                     : 'Safety case drafted.');
      qc.invalidateQueries({ queryKey: ['ai-safety', 'safety-cases'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = capabilityId && title.trim() && hazardAnalysis.trim() &&
                mitigations.trim() && validation.trim() && postMarket.trim();

  return (
    <Dialog fullScreen={isMobile} open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        {isReadOnly ? 'View safety case' : isEdit ? 'Edit safety case' : 'New safety case'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Capability"
                       value={capabilityId}
                       onChange={(e) => setCapabilityId(e.target.value)}
                       disabled={isReadOnly}>
              {capabilities.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.display_name} ({c.code})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Title"
                       value={title} onChange={(e) => setTitle(e.target.value)}
                       disabled={isReadOnly} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required multiline minRows={4}
                       label="Hazard analysis"
                       value={hazardAnalysis}
                       onChange={(e) => setHazardAnalysis(e.target.value)}
                       disabled={isReadOnly}
                       helperText="What can go wrong? List failure modes and their potential clinical impact." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required multiline minRows={4}
                       label="Mitigations"
                       value={mitigations}
                       onChange={(e) => setMitigations(e.target.value)}
                       disabled={isReadOnly}
                       helperText="How are the hazards reduced or detected? Include human-in-the-loop checks." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required multiline minRows={3}
                       label="Validation evidence"
                       value={validation}
                       onChange={(e) => setValidation(e.target.value)}
                       disabled={isReadOnly}
                       helperText="Links to evaluation runs, benchmarks, prospective studies." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required multiline minRows={3}
                       label="Post-market monitoring"
                       value={postMarket}
                       onChange={(e) => setPostMarket(e.target.value)}
                       disabled={isReadOnly}
                       helperText="What signals do we watch (rejection rates, edit rates, drift)? When do we trigger review?" />
          </Grid>
          {!isReadOnly && safetyCase?.status === 'draft' && (
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <input type="checkbox"
                       id="submit-checkbox"
                       checked={submit}
                       onChange={(e) => setSubmit(e.target.checked)} />
                <Typography component="label" htmlFor="submit-checkbox" variant="body2">
                  Mark as <strong>submitted</strong> for approval (locks editing).
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isReadOnly && (
          <Button variant="contained"
                  disabled={!valid || saveMut.isPending}
                  onClick={() => saveMut.mutate()}>
            {submit ? 'Save & submit' : isEdit ? 'Save' : 'Save draft'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
